import { useCallback, useEffect, useState } from "react";
import { Loader2, Send, RefreshCw, Mail, Ban, RotateCcw, Eye, LayoutDashboard, FileText, Settings2, FlaskConical, AlertTriangle, CheckCircle2, History } from "lucide-react";
import { AdminShell } from "../components/AdminShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pendiente", cls: "bg-hz-gray/30 text-hz-ink" },
  pending_configuration: { label: "Sin configurar", cls: "bg-hz-warning/10 text-hz-warning" },
  queued: { label: "En cola", cls: "bg-hz-cyan/10 text-hz-blue" },
  sending: { label: "Enviando", cls: "bg-hz-cyan/10 text-hz-blue" },
  sent: { label: "Enviado", cls: "bg-hz-success/10 text-hz-success" },
  delivered: { label: "Entregado", cls: "bg-hz-success/10 text-hz-success" },
  bounced: { label: "Rebotado", cls: "bg-hz-error/10 text-hz-error" },
  failed: { label: "Fallido", cls: "bg-hz-error/10 text-hz-error" },
  suppressed: { label: "Suprimido", cls: "bg-hz-gray/30 text-hz-ink/70" },
  cancelled: { label: "Cancelado", cls: "bg-hz-gray/30 text-hz-ink/70" },
};

const TYPE_LABELS = {
  registration_created_individual: "Registro individual creado",
  registration_created_group: "Registro grupal/familiar creado",
  manual_registration_created: "Registro manual creado",
  receipt_uploaded: "Comprobante recibido",
  payment_approved_individual: "Pago individual aprobado",
  payment_approved_group: "Pago de grupo aprobado",
  payment_rejected: "Pago rechazado",
  payment_correction_requested: "Corrección de comprobante",
  qr_enabled: "QR habilitado",
  access_updated: "Accesos actualizados",
  workshop_updated: "Taller actualizado",
  child_photo_pending: "Foto infantil pendiente",
  child_photo_completed: "Foto infantil completada",
  child_care_ineligible_reminder: "Recordatorio requisito infantil",
  event_reminder: "Recordatorio de evento",
  registration_cancelled: "Registro cancelado",
  incident_resolved_optional: "Incidencia resuelta",
  test_email: "Correo de prueba",
};

const StatusBadgeMini = ({ status }) => {
  const m = STATUS_META[status] || { label: status, cls: "bg-hz-gray/30 text-hz-ink" };
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${m.cls}`} data-testid={`notif-status-${status}`}>{m.label}</span>;
};

const NotifContent = ({ user }) => {
  const isAdmin = user.role === "admin";
  const [tab, setTab] = useState("resumen");
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ type: "all", status: "all", q: "" });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editTpl, setEditTpl] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => {
    api.get("/admin/notifications/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  const loadList = useCallback(() => {
    const params = {};
    if (filters.type !== "all") params.type = filters.type;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.q.trim()) params.q = filters.q.trim();
    api.get("/admin/notifications", { params }).then((r) => setList(r.data.notifications || [])).catch((e) => toast.error(apiError(e)));
  }, [filters]);

  const loadTemplates = useCallback(() => {
    if (!isAdmin) return;
    api.get("/admin/notification-templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, [isAdmin]);

  const loadSettings = useCallback(() => {
    if (!isAdmin) return;
    api.get("/admin/notification-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => { loadSummary(); loadList(); }, [loadSummary, loadList]);
  useEffect(() => { if (tab === "plantillas") loadTemplates(); if (tab === "configuracion") loadSettings(); }, [tab, loadTemplates, loadSettings]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ id });
    try {
      const { data } = await api.get(`/admin/notifications/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(apiError(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const action = async (id, act, label) => {
    if (act === "resend" && !window.confirm("¿Reenviar esta notificación al destinatario?")) return;
    try {
      await api.post(`/admin/notifications/${id}/${act}`);
      toast.success(label);
      setDetail(null);
      loadList();
      loadSummary();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runJob = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/notifications/${kind}`);
      toast.success(`Proceso completado: ${JSON.stringify(data.result ?? data)}`);
      loadSummary();
      loadList();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      const payload = { ...settings };
      delete payload.provider_status;
      if (typeof payload.reminder_windows_hours === "string") payload.reminder_windows_hours = payload.reminder_windows_hours.split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x));
      if (typeof payload.care_reminder_days === "string") payload.care_reminder_days = payload.care_reminder_days.split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x));
      await api.put("/admin/notification-settings", payload);
      toast.success("Configuración guardada");
      loadSettings();
      loadSummary();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/test-email");
      toast.success(data.status === "sent" ? `Correo de prueba enviado a ${data.recipient}` : `Correo de prueba registrado con estado: ${STATUS_META[data.status]?.label || data.status}`);
      loadSummary();
      loadList();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/notification-templates/${editTpl.id}`, { subject_template: editTpl.subject_template, body_template: editTpl.body_template });
      toast.success("Plantilla actualizada (nueva versión)");
      setEditTpl(null);
      loadTemplates();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="notif-tabs">
      <TabsList className="flex h-auto flex-wrap justify-start gap-1 bg-white p-1">
        <TabsTrigger value="resumen" data-testid="notif-tab-resumen" className="font-semibold"><LayoutDashboard className="mr-1.5 h-4 w-4" />Resumen</TabsTrigger>
        <TabsTrigger value="historial" data-testid="notif-tab-historial" className="font-semibold"><History className="mr-1.5 h-4 w-4" />Historial</TabsTrigger>
        {isAdmin && <TabsTrigger value="plantillas" data-testid="notif-tab-plantillas" className="font-semibold"><FileText className="mr-1.5 h-4 w-4" />Plantillas</TabsTrigger>}
        {isAdmin && <TabsTrigger value="configuracion" data-testid="notif-tab-config" className="font-semibold"><Settings2 className="mr-1.5 h-4 w-4" />Configuración</TabsTrigger>}
      </TabsList>

      <TabsContent value="resumen" className="mt-6 space-y-6" data-testid="notif-resumen">
        {summary?.provider && !summary.provider.ready && (
          <p className="flex items-start gap-2 rounded-xl border border-hz-warning/40 bg-hz-warning/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="notif-provider-alert" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-hz-warning" />
            El proveedor de correo no está activo. Las notificaciones se registran como «Sin configurar» y ninguna operación del sistema se ve afectada. Actívalo en Configuración.
          </p>
        )}
        {summary?.provider?.test_mode && summary?.provider?.ready && (
          <p className="rounded-xl border border-hz-cyan/40 bg-hz-cyan/5 px-4 py-3 text-sm font-medium text-hz-ink" data-testid="notif-test-mode-alert">
            <strong>Modo prueba activo</strong>{summary.provider.locked_by_env ? " — impuesto por este entorno (EMAIL_TEST_MODE). T" : " — t"}odos los correos se redirigen a {summary.provider.test_recipient || "(sin destinatario de prueba configurado)"} con prefijo [PRUEBA]. En modo real, los asuntos salen limpios y el destino es el del asistente.
          </p>
        )}
        {!summary ? (
          <div className="flex items-center gap-2 py-10 text-hz-navy"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
              {[
                { label: "Enviadas hoy", value: summary.today_sent, testId: "notif-today-sent" },
                { label: "Pendientes", value: summary.pending, testId: "notif-pending" },
                { label: "Fallidas", value: summary.failed, testId: "notif-failed" },
                { label: "Reintentos programados", value: summary.retries_scheduled, testId: "notif-retries" },
                { label: "Suprimidas", value: summary.suppressed, testId: "notif-suppressed" },
              ].map((m) => (
                <div key={m.label} className="rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid={m.testId}>
                  <Mail className="h-5 w-5 text-hz-cyan" />
                  <p className="mt-2 text-2xl font-extrabold text-hz-navy">{m.value}</p>
                  <p className="text-xs font-semibold text-hz-ink/60">{m.label}</p>
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => runJob("process-queue")} disabled={busy} data-testid="notif-process-queue-btn" className="font-bold text-hz-navy">
                  <RefreshCw className="mr-1.5 h-4 w-4" /> Procesar cola ahora
                </Button>
                <Button variant="outline" size="sm" onClick={() => runJob("run-reminders")} disabled={busy} data-testid="notif-run-reminders-btn" className="font-bold text-hz-navy">
                  <Send className="mr-1.5 h-4 w-4" /> Ejecutar recordatorios
                </Button>
              </div>
            )}
            <div className="rounded-2xl border border-hz-gray/40 bg-white" data-testid="notif-recent">
              <p className="border-b border-hz-gray/40 px-4 py-3 text-sm font-bold text-hz-navy">Últimas notificaciones</p>
              {summary.recent.length === 0 ? (
                <p className="px-4 py-6 text-sm text-hz-ink/60">Aún no hay notificaciones registradas.</p>
              ) : (
                <ul className="divide-y divide-hz-gray/30">
                  {summary.recent.map((n) => (
                    <li key={n.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`notif-recent-${n.id}`}>
                      <span className="font-semibold text-hz-ink">{TYPE_LABELS[n.notification_type] || n.notification_type}</span>
                      <span className="text-xs text-hz-ink/60">{n.recipient_masked}</span>
                      <StatusBadgeMini status={n.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </TabsContent>

      <TabsContent value="historial" className="mt-6 space-y-4" data-testid="notif-historial">
        <div className="flex flex-wrap gap-2">
          <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
            <SelectTrigger className="w-56" data-testid="notif-filter-type"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-44" data-testid="notif-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Folio o correo…" className="w-52" data-testid="notif-filter-q" />
          <Button onClick={loadList} data-testid="notif-filter-apply" className="bg-hz-navy font-bold text-white">Filtrar</Button>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-hz-gray/40 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-bold text-hz-navy">Tipo</TableHead>
                <TableHead className="font-bold text-hz-navy">Destinatario</TableHead>
                <TableHead className="font-bold text-hz-navy">Folio</TableHead>
                <TableHead className="font-bold text-hz-navy">Estado</TableHead>
                <TableHead className="font-bold text-hz-navy">Intentos</TableHead>
                <TableHead className="font-bold text-hz-navy">Fecha</TableHead>
                <TableHead className="font-bold text-hz-navy">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((n) => (
                <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                  <TableCell className="text-xs font-semibold">{TYPE_LABELS[n.notification_type] || n.notification_type}</TableCell>
                  <TableCell className="text-xs">{n.recipient_masked || "—"}</TableCell>
                  <TableCell className="text-xs font-bold text-hz-navy">{n.folio || "—"}</TableCell>
                  <TableCell><StatusBadgeMini status={n.status} />{n.error_message_safe && <p className="mt-0.5 text-xs text-hz-ink/50">{n.error_message_safe}</p>}</TableCell>
                  <TableCell className="text-xs">{n.attempts}</TableCell>
                  <TableCell className="text-xs">{new Date(n.created_at).toLocaleString("es-MX")}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" onClick={() => openDetail(n.id)} data-testid={`notif-detail-${n.id}`} className="font-semibold"><Eye className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-hz-ink/60" data-testid="notif-empty">Sin notificaciones con esos filtros.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-navy" data-testid="notif-detail-title">
                {detail?.notification_type ? (TYPE_LABELS[detail.notification_type] || detail.notification_type) : "Detalle"}
              </DialogTitle>
              <DialogDescription className="text-xs text-hz-ink/60">Detalle de la notificación, vista previa con enlaces enmascarados e historial de intentos.</DialogDescription>
            </DialogHeader>
            {detailLoading || !detail?.status ? (
              <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><p className="text-xs text-hz-ink/60">Destinatario</p><p className="font-semibold">{detail.recipient}</p></div>
                  <div><p className="text-xs text-hz-ink/60">Estado</p><StatusBadgeMini status={detail.status} /></div>
                  <div><p className="text-xs text-hz-ink/60">Intentos</p><p className="font-semibold">{detail.attempts} / {detail.max_attempts}</p></div>
                  <div><p className="text-xs text-hz-ink/60">Folio</p><p className="font-semibold">{detail.folio || "—"}</p></div>
                </div>
                {detail.error_message_safe && (
                  <p className="rounded-lg bg-hz-error/5 px-3 py-2 text-xs font-semibold text-hz-error" data-testid="notif-detail-error">{detail.error_message_safe}</p>
                )}
                <div>
                  <p className="text-xs font-bold text-hz-navy">Asunto</p>
                  <p className="text-sm font-semibold text-hz-ink" data-testid="notif-detail-subject">{detail.subject}</p>
                </div>
                {detail.html_preview && (
                  <div>
                    <p className="text-xs font-bold text-hz-navy">Vista previa (enlaces enmascarados)</p>
                    <iframe title="preview" sandbox="" srcDoc={detail.html_preview} className="mt-1 h-72 w-full rounded-xl border border-hz-gray/40" data-testid="notif-detail-preview" />
                  </div>
                )}
                {detail.attempts_list?.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-hz-navy">Intentos</p>
                    <ul className="mt-1 space-y-1 text-xs text-hz-ink/70">
                      {detail.attempts_list.map((a) => (
                        <li key={a.id}>#{a.attempt_number} · {a.status} · {new Date(a.started_at).toLocaleString("es-MX")}{a.error_message_safe ? ` · ${a.error_message_safe}` : ""}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 border-t border-hz-gray/40 pt-4">
                  {["sent", "delivered", "failed", "bounced"].includes(detail.status) && (
                    <Button size="sm" onClick={() => action(detail.id, "resend", "Notificación reenviada a la cola")} data-testid="notif-resend-btn" className="bg-hz-cyan font-bold text-white">
                      <Send className="mr-1.5 h-4 w-4" /> Reenviar
                    </Button>
                  )}
                  {isAdmin && detail.status === "failed" && (
                    <Button size="sm" variant="outline" onClick={() => action(detail.id, "retry", "Reintento programado")} data-testid="notif-retry-btn" className="font-bold text-hz-navy">
                      <RotateCcw className="mr-1.5 h-4 w-4" /> Reintentar
                    </Button>
                  )}
                  {isAdmin && ["pending", "queued", "pending_configuration"].includes(detail.status) && (
                    <Button size="sm" variant="outline" onClick={() => action(detail.id, "cancel", "Notificación cancelada")} data-testid="notif-cancel-btn" className="font-bold text-hz-error">
                      <Ban className="mr-1.5 h-4 w-4" /> Cancelar
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </TabsContent>

      {isAdmin && (
        <TabsContent value="plantillas" className="mt-6" data-testid="notif-plantillas">
          <div className="space-y-3">
            {templates.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-hz-gray/40 bg-white p-4" data-testid={`tpl-row-${t.code}`}>
                <div>
                  <p className="text-sm font-bold text-hz-navy">{TYPE_LABELS[t.code] || t.name}</p>
                  <p className="text-xs text-hz-ink/60">{t.code} · v{t.version}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink">
                    <Switch checked={t.is_active} onCheckedChange={async () => {
                      if (t.is_active && !window.confirm(`¿Desactivar la plantilla «${t.name}»? Las notificaciones de este tipo quedarán suprimidas.`)) return;
                      try { await api.post(`/admin/notification-templates/${t.id}/toggle`); loadTemplates(); } catch (e) { toast.error(apiError(e)); }
                    }} data-testid={`tpl-toggle-${t.code}`} />
                    {t.is_active ? "Activa" : "Inactiva"}
                  </label>
                  <Button variant="outline" size="sm" onClick={async () => {
                    try { const { data } = await api.get(`/admin/notification-templates/${t.id}/preview`); setPreviewTpl(data); } catch (e) { toast.error(apiError(e)); }
                  }} data-testid={`tpl-preview-${t.code}`} className="font-semibold"><Eye className="mr-1 h-3.5 w-3.5" /> Vista previa</Button>
                  <Button variant="outline" size="sm" onClick={() => setEditTpl({ ...t })} data-testid={`tpl-edit-${t.code}`} className="font-semibold">Editar</Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    if (!window.confirm("¿Restaurar la plantilla predeterminada? Se perderán los cambios.")) return;
                    try { await api.post(`/admin/notification-templates/${t.id}/restore`); toast.success("Plantilla restaurada"); loadTemplates(); } catch (e) { toast.error(apiError(e)); }
                  }} data-testid={`tpl-restore-${t.code}`} className="font-semibold text-hz-error">Restaurar</Button>
                </div>
              </div>
            ))}
          </div>

          <Dialog open={!!editTpl} onOpenChange={(o) => !o && setEditTpl(null)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-bold text-hz-navy">Editar plantilla — {editTpl?.name}</DialogTitle>
                <DialogDescription className="text-xs text-hz-ink/60">Edita el asunto y el cuerpo usando las variables disponibles. Cada guardado crea una nueva versión.</DialogDescription>
              </DialogHeader>
              {editTpl && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold">Asunto</Label>
                    <Input value={editTpl.subject_template} onChange={(e) => setEditTpl({ ...editTpl, subject_template: e.target.value })} data-testid="tpl-edit-subject" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Cuerpo (una línea por párrafo; usa {"{{variable}}"})</Label>
                    <Textarea value={editTpl.body_template} onChange={(e) => setEditTpl({ ...editTpl, body_template: e.target.value })} rows={10} data-testid="tpl-edit-body" className="mt-1 font-mono text-xs" />
                  </div>
                  <p className="rounded-lg bg-hz-bg px-3 py-2 text-xs text-hz-ink/70" data-testid="tpl-edit-vars">
                    Variables disponibles: {(editTpl.variables || []).map((v) => `{{${v}}}`).join(" ")}
                  </p>
                  <Button onClick={saveTemplate} disabled={busy} data-testid="tpl-save-btn" className="bg-hz-cyan font-bold text-white">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Guardar (nueva versión)
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={!!previewTpl} onOpenChange={(o) => !o && setPreviewTpl(null)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-bold text-hz-navy">Vista previa — {previewTpl?.name}</DialogTitle>
                <DialogDescription className="text-xs text-hz-ink/60">Render con datos de ejemplo y branding oficial de Horizonte 2026.</DialogDescription>
              </DialogHeader>
              {previewTpl && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-hz-ink" data-testid="tpl-preview-subject">Asunto: {previewTpl.subject}</p>
                  <iframe title="tpl-preview" sandbox="" srcDoc={previewTpl.html} className="h-96 w-full rounded-xl border border-hz-gray/40" data-testid="tpl-preview-frame" />
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
      )}

      {isAdmin && (
        <TabsContent value="configuracion" className="mt-6" data-testid="notif-config">
          {!settings ? (
            <div className="flex items-center gap-2 py-10 text-hz-navy"><Loader2 className="h-5 w-5 animate-spin" /> Cargando…</div>
          ) : (
            <div className="max-w-2xl space-y-5 rounded-2xl border border-hz-gray/40 bg-white p-6">
              <div className="flex items-center justify-between rounded-xl bg-hz-bg px-4 py-3" data-testid="config-provider-status">
                <div>
                  <p className="text-sm font-bold text-hz-navy">Estado del proveedor</p>
                  <p className="text-xs text-hz-ink/60">Proveedor: {settings.provider} · Credencial: {settings.provider_status?.key_configured ? "configurada" : "no configurada"}</p>
                </div>
                {settings.provider_status?.ready ? (
                  <span className="flex items-center gap-1.5 text-xs font-bold text-hz-success"><CheckCircle2 className="h-4 w-4" /> Activo</span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs font-bold text-hz-warning"><AlertTriangle className="h-4 w-4" /> Inactivo</span>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                  <Switch checked={!!settings.email_enabled} onCheckedChange={(v) => setSettings({ ...settings, email_enabled: v === true })} data-testid="config-email-enabled" />
                  Correo habilitado
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                  <Switch
                    checked={!!settings.test_mode}
                    onCheckedChange={(v) => setSettings({ ...settings, test_mode: v === true })}
                    disabled={!!settings.test_mode_locked_by_env}
                    data-testid="config-test-mode"
                  />
                  Modo prueba (redirige todo)
                  {settings.test_mode_locked_by_env && (
                    <span className="ml-2 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-hz-warning/15 px-2 py-0.5 text-[10px] font-bold text-hz-warning" data-testid="config-test-mode-locked-badge">
                      Impuesto por entorno
                    </span>
                  )}
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                  <Switch checked={!!settings.reminders_enabled} onCheckedChange={(v) => setSettings({ ...settings, reminders_enabled: v === true })} data-testid="config-reminders-enabled" />
                  Recordatorios habilitados
                </label>
                <div>
                  <Label className="text-xs font-semibold">Proveedor</Label>
                  <Select value={settings.provider} onValueChange={(v) => setSettings({ ...settings, provider: v })}>
                    <SelectTrigger className="mt-1" data-testid="config-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resend" data-testid="config-provider-resend">Resend (gestionado)</SelectItem>
                      <SelectItem value="disabled" data-testid="config-provider-disabled">Desactivado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label className="text-xs font-semibold">Nombre del remitente</Label><Input value={settings.from_name || ""} onChange={(e) => setSettings({ ...settings, from_name: e.target.value })} data-testid="config-from-name" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Correo remitente (informativo)</Label><Input value={settings.from_email || ""} onChange={(e) => setSettings({ ...settings, from_email: e.target.value })} data-testid="config-from-email" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Responder a</Label><Input value={settings.reply_to_email || ""} onChange={(e) => setSettings({ ...settings, reply_to_email: e.target.value })} data-testid="config-reply-to" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Correo de soporte (footer)</Label><Input value={settings.support_email || ""} onChange={(e) => setSettings({ ...settings, support_email: e.target.value })} data-testid="config-support-email" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Destinatario de pruebas</Label><Input value={settings.test_recipient_email || ""} onChange={(e) => setSettings({ ...settings, test_recipient_email: e.target.value })} data-testid="config-test-recipient" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Zona horaria del evento</Label><Input value={settings.event_timezone || ""} onChange={(e) => setSettings({ ...settings, event_timezone: e.target.value })} data-testid="config-timezone" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Recordatorios del evento (horas antes, separadas por coma)</Label><Input value={Array.isArray(settings.reminder_windows_hours) ? settings.reminder_windows_hours.join(", ") : settings.reminder_windows_hours} onChange={(e) => setSettings({ ...settings, reminder_windows_hours: e.target.value })} data-testid="config-reminder-hours" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Recordatorio requisito infantil (días antes)</Label><Input value={Array.isArray(settings.care_reminder_days) ? settings.care_reminder_days.join(", ") : settings.care_reminder_days} onChange={(e) => setSettings({ ...settings, care_reminder_days: e.target.value })} data-testid="config-care-days" className="mt-1" /></div>
                <div><Label className="text-xs font-semibold">Máximo de reintentos</Label><Input type="number" min="1" max="10" value={settings.max_attempts || 3} onChange={(e) => setSettings({ ...settings, max_attempts: parseInt(e.target.value, 10) || 3 })} data-testid="config-max-attempts" className="mt-1" /></div>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-hz-gray/40 pt-4">
                <Button onClick={saveSettings} disabled={busy} data-testid="config-save-btn" className="bg-hz-navy font-bold text-white">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Guardar configuración
                </Button>
                <Button onClick={sendTest} disabled={busy} variant="outline" data-testid="config-test-email-btn" className="font-bold text-hz-navy">
                  <FlaskConical className="mr-1.5 h-4 w-4" /> Enviar correo de prueba
                </Button>
              </div>
              <p className="text-xs text-hz-ink/50">Las claves del proveedor se gestionan por variables de entorno del servidor y nunca se muestran aquí.</p>
              {settings.test_mode_locked_by_env && (
                <div className="rounded-xl border border-hz-warning/40 bg-hz-warning/5 px-4 py-3 text-xs text-hz-ink" data-testid="config-env-lock-note">
                  <strong>Modo de prueba impuesto por este entorno.</strong> Fuente: variable <code>EMAIL_TEST_MODE=true</code>. Destinatario forzado: {settings.env_test_recipient || "(no configurado)"}.
                  Para operar en modo real, retira esta variable en el entorno autorizado y vuelve a desplegar; la configuración guardada se respetará.
                </div>
              )}
              <div className="rounded-xl border border-hz-gray/40 bg-hz-bg px-4 py-3 text-xs text-hz-ink" data-testid="config-effective-state">
                <p className="font-bold text-hz-navy">Estado efectivo</p>
                <ul className="mt-1 space-y-0.5">
                  <li><strong>Modo:</strong> {settings.test_mode ? "Prueba (redirige y antepone [PRUEBA])" : "Real (envío directo al asistente)"} · fuente: {settings.source_test_mode}</li>
                  <li><strong>Proveedor:</strong> {settings.provider} {settings.provider_status?.ready ? "· listo" : "· no listo"}</li>
                  <li><strong>Remitente efectivo:</strong> {settings.provider_status?.resend_from_email || settings.from_email || "(no configurado)"}</li>
                  <li><strong>Destinatario de pruebas:</strong> {settings.test_recipient_email || "(no configurado)"}</li>
                </ul>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-hz-gray/40 pt-3">
                <Button
                  variant="outline"
                  disabled
                  title="Deshabilitado: el flujo masivo fue retirado. Usa dry-run + lote acotado por API hasta nueva UI."
                  data-testid="config-cancel-test-queue-btn"
                  className="font-bold text-hz-ink/40 cursor-not-allowed"
                >
                  <Ban className="mr-1.5 h-4 w-4" /> Cancelar cola de pruebas (deshabilitado)
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      )}
    </Tabs>
  );
};

const AdminNotificaciones = () => (
  <AdminShell roles={["admin", "welcome_coordinator"]} title="Notificaciones" testId="admin-notificaciones"
    subtitle="Centro de correos transaccionales: historial, reintentos, plantillas y configuración del proveedor.">
    {(user) => <NotifContent user={user} />}
  </AdminShell>
);

export default AdminNotificaciones;
